import {
  EditPhaseKinds,
  UpdateKinds,
  ExternalScriptRequestInformation,
  ExternalScriptManager,
  ExternalScriptParameter,
  Section,
} from "../../../../domain/document";
import {
  IItem,
  instanceOfIExternalDataApplicable,
  IExternalDataApplicable,
  instanceOfIExternalScriptAssociatable,
  IExternalScriptAssociatable,
  instanceOfIExternalScriptExecutable,
  RelationalActionParameter,
  IExternalScriptExecutable,
  instanceOfIFreeInputable,
  instanceOfIExternalScriptImageAssociatable,
  IExternalScriptImageAssociatable,
  instanceOfIRelatedExternalScriptExecutable,
  instanceOfIObjectHeightAdjustable,
  instanceOfIMksTemplateExecutable,
  IMksTemplateExecutable,
} from "../../../../domain/document/items";
import { ExternalScriptExecuteResult } from "../../../../domain/localhost/items";
import {
  IDocumentEditable,
} from "..";
import { AsyncUtility, BrowserUtility } from "../../../../utilities";
import store from "../../../../store";
import { IConfiguration } from "../../../../domain/configuration";
import { LoggerFactory } from "../../../../utilities/logger";

export class ExternalScriptRunner {
  constructor(public source: IDocumentEditable, requestedScripts?: ExternalScriptRequestInformation[]) {
    this._requestedScripts = requestedScripts
      ? requestedScripts
      : [];
  }

  private readonly _maxExecuteCountAtOnce = 100;
  private _requestedScripts: ExternalScriptRequestInformation[];
  private _requestHistory: ExternalScriptRequestInformation[] = [];

  public hasError: boolean = false;

  /**
   * 登録のタイミングでの外部処理がキャンセルされたかどうかを判定するためのフラグ
   * 編集中の実行では返却する必要が無いため、判定が必要な時だけ見るようにしている
   */
  public hasCancel: boolean = false;

  public isProcessing: boolean = false;

  // 外部処理の実行を制限するかどうか
  public shouldRestrictScript: boolean;

  // 実行可能な外部処理を指定
  public allowedScripts: string[];

  /**
   * オブジェクトの値の更新を契機として他の外部処理をキックする
   */
  addScriptExecuteRequest(itemSetId: number, updated: IItem): void {
    // 外部スクリプト関連の実装を入れるのは抵抗があるが、現状癒着が激しくて剥がせない.
    // 詳細を明らかにしてから対処.
    // (値が更新されたトリガーさえ取れれば剥がせそうだが。。。)
    if (!instanceOfIRelatedExternalScriptExecutable(updated)) { return; }
    if (!updated.scriptExecuteParameters || updated.scriptExecuteParameters.length === 0) { return; }
    for (const parameter of updated.scriptExecuteParameters) {
      const found = this.findExecuteTarget(itemSetId, parameter);
      if (!found) {
        console.warn("ExternalScriptExecutableBaseItem not found!!!");
        continue;
      }
      this.pushExecuteRequest(itemSetId, found.privateId);
    }
  }

  public pushExecuteRequest(itemSetId: number, privateId: number): void {
    const info = new ExternalScriptRequestInformation();
    info.itemSetId = itemSetId;
    info.privateId = privateId;
    this._requestedScripts.push(info);
  }

  private findExecuteTarget(itemSetId: number, parameter: RelationalActionParameter): IItem {
    // 徳大はpageKeyがnullになっているので注意すること
    const itemSet = this.source.itemSets[itemSetId];
    const specifiedSection = this.source.sections
      .filter(s => s.itemSetId === itemSetId && (s.pageKey != null) && s.pageKey === parameter.pageKey)
      .pop();
    
    if (specifiedSection) {
      const sectionItems = specifiedSection.itemPrivateIds.map(privateId => itemSet.items[privateId]);
      const found = this.findExecuteTargetFromItems(parameter, sectionItems);
      if (found) { return found; }
    }
    // 指定されたpageKeyに見つからなかったら全ページから探索する
    return this.findExecuteTargetFromItems(parameter, itemSet.items);
  }

  private findExecuteTargetFromItems(parameter: RelationalActionParameter, items: IItem[]): IItem {
    for (const item of items) {
      if (!instanceOfIExternalScriptExecutable(item)) { continue; }
      if (this.shouldSkip(item.exePath)) { return; }
      if (parameter.objectId) {
        // objectIdが一致するかどうか
        if (item.id === parameter.objectId) {
          return item;
        }
      } else {
        // TODO: objectId以外での一致の挙動の確認
        // ※外部処理・外部データにはobj-idがセットされているはず
        return null;
      }
    }
    return null;
  }

  public get hasRequestedScripts(): boolean {
    return (this._requestedScripts && this._requestedScripts.length !== 0);
  }

  public async executeStackedScriptsAsyncWithResult(param: ExternalScriptParameter): Promise<boolean> {
    await this.executeStackedScriptsAsync(param);
    const isSuccess = !this.hasError;
    this.hasError = false;
    return isSuccess;
  }

  public async executeStackedScriptsAsync(param: ExternalScriptParameter): Promise<void> {
    let counter = 0;
    while (this.hasRequestedScripts) {
      if (counter > this._maxExecuteCountAtOnce) {
        this.hasError = true;
        break;
      }
      await this.executeStackedScriptsWithHistoryAsync(param);
      counter++;
    }
  }

  private async executeStackedScriptsWithHistoryAsync(param: ExternalScriptParameter): Promise<void> {
    // キーを連打すると同じ要求を何度も実行してしまうので対策している
    const requests = this.organizeStackedRequests(this._requestedScripts.concat());
    for (const request of requests) {
      this._requestHistory.push(request);
    }
    this._requestedScripts = [];
    return this.executeRequestedScriptsAsync(param, requests);
  }

  private async executeRequestedScriptsAsync(param: ExternalScriptParameter, requests: ExternalScriptRequestInformation[]): Promise<void> {
    // TODO: 処理中フラグの見直し
    this.isProcessing = true;
    try {
      const config = store.getters.configuration as IConfiguration;
      if (config.behavior.document.editor.externalScript.shouldExecuteAsBatch && requests.length > 1) {
        await this.executeStackedScriptsAsyncAsBatch(param, requests);
      } else {
        await this.executeStackedScriptsAsyncAsContinuous(param, requests);
      }
    } finally {
      // これだと前の要求が先に終わった時にケア出来ないが、何もチェックしないよりはまし
      this.isProcessing = false;
    }
  }

  /**
   * 同じ外部処理・外部データに対する実行要求がある場合に、後ろ側の実行要求だけとなるように整理する
   */
  private organizeStackedRequests(requests: ExternalScriptRequestInformation[]): ExternalScriptRequestInformation[] {
    const organizedRequests: ExternalScriptRequestInformation[] = [];
    for (let index = requests.length - 1; index >= 0; --index) {
      const request = requests[index];
      
      // 同じ実行要求を抑制する
      const found = organizedRequests.find(r => (r.itemSetId === request.itemSetId) && (r.privateId === request.privateId));
      if (found) { continue; }

      // 配列の先頭に追加する
      organizedRequests.unshift(request);
    }
    return organizedRequests;
  }

  private async executeStackedScriptsAsyncAsBatch(param: ExternalScriptParameter, requests: ExternalScriptRequestInformation[]): Promise<void> {
    // ExternalScriptManagerの処理の都合で、itemSet分けたほうが修正量が減るので、itemSetIdで分けて実装する。
    // 現実的にはほとんどのケースでitemSetIdはひとつだけになるはず。
    const itemSetIds = this.extractItemSetIds(requests);
    for (const itemSetId of itemSetIds) {
      const executeRequests: ExternalScriptRequestInformation[] = [];
      for (const request of requests) {
        if (request.itemSetId !== itemSetId) { continue; }
        executeRequests.push(request);
      }
      try {
        await this.executeStackedScriptItemsAsyncAsBatch(param, itemSetId, executeRequests);
      } catch (e) {
        console.warn(e);
        this.hasError = true;
      }
    }
  }

  private extractItemSetIds(requests: ExternalScriptRequestInformation[]): number[] {
    const itemSetIds: number[] = [];
    for (const request of requests) {
      if (itemSetIds.indexOf(request.itemSetId) >= 0) { continue; }
      itemSetIds.push(request.itemSetId);
    }
    return itemSetIds;
  }

  private async executeStackedScriptItemsAsyncAsBatch(param: ExternalScriptParameter, itemSetId: number, requests: ExternalScriptRequestInformation[]): Promise<void> {
    if (requests.length === 0) { return; }
    const items: IExternalScriptExecutable[] = [];
    for (const request of requests) {
      if (request.itemSetId !== itemSetId) { throw new Error("itemSetIdが一致していません。"); }
      const item = this.source.itemSets[request.itemSetId].items[request.privateId];
      if (!instanceOfIExternalScriptExecutable(item)) { continue; }
      items.push(item);
    }
    const itemSet = this.source.itemSets[itemSetId];
    const sections = this.source.sections.filter(s => s.itemSetId === itemSetId);
    const manager = new ExternalScriptManager(itemSet, sections);
    const batchResults = await manager.executeScripts(param, items);
    if (!batchResults) {
      this.hasError = true;
      return;
    }
    for (const batchResult of batchResults) {
      const result = batchResult.result;
      if (result && result.isCancel) {
        this.hasCancel = true;
      }
      if (!result || !result.isComplete) {
        this.hasError = true;
        continue;
      }
      const item = batchResult.request.source;
      const sourceRequest = requests.find(r => r.privateId === item.privateId);
      if (this.isConflictedRequest(sourceRequest)) { return; }

      result.name = item.name;
      if (instanceOfIExternalDataApplicable(item)) {
        this.applyExternalDataResultValues(result, itemSetId, item.privateId);
      } else if (instanceOfIExternalScriptExecutable(item)) {
        this.applyExternalProcessResultValues(result, itemSetId, param);
      }
    }
  }

  private async executeStackedScriptsAsyncAsContinuous(param: ExternalScriptParameter, requests: ExternalScriptRequestInformation[]): Promise<void> {
    const executedScripts: ExternalScriptRequestInformation[] = [];
    for (const request of requests) {
      const item = this.source.itemSets[request.itemSetId].items[request.privateId];
      try {
        if (executedScripts.length !== 0) {
          const config = store.getters.configuration as IConfiguration;
          if (!config.behavior.localhost.internalScript.isEnable) {
            // ChromeはUrlSchemeを叩く間隔が短いと対象のEXEを起動できない現象が発生する。
            // 3.5秒程度の間隔を空けると起動できる模様なので、無理やり間隔を空けている。
            // ※IEもある程度の間隔を空ける必要がある模様・・
            console.log("UrlScheme対策でsleepします。");
            const sleepTime = BrowserUtility.isChromiumBrowser()
              ? 4000
              : 2000;
            await AsyncUtility.sleep(sleepTime);
            console.log("UrlScheme対策でsleepしました。");
          }
        }
        await this.executeExternalScriptAsync(item, request.itemSetId, param, request);
        executedScripts.push(request);
        // 連続して処理するとIEでPOST間隔が数秒開く現象あり。sleep入れると改善することがあるが、しないこともある・・
        await AsyncUtility.sleep(50);
      } catch (e) {
        console.warn(e);
        this.hasError = true;
      }
    }
  }

  async executeExternalScriptAsync(item: IItem, itemSetId: number, param: ExternalScriptParameter, request: ExternalScriptRequestInformation): Promise<void> {
    if (!instanceOfIExternalScriptExecutable(item)) { return; }
    if (this.shouldSkip(item.exePath)) { return; }
    const itemSet = this.source.itemSets[itemSetId];
    const sections = this.source.sections.filter(s => s.itemSetId === itemSetId);
    const manager = new ExternalScriptManager(itemSet, sections);
    const result = await manager.executeScript(param, item);
    if (!result || !result.isComplete || !result.scriptLinkResults) { throw new Error("スクリプトが正常に完了していません。"); }
    if (result.isCancel) {
      this.hasCancel = true;
    }
    if (this.isConflictedRequest(request)) { return; }
    result.name = item.name;
    if (instanceOfIExternalDataApplicable(item)) {
      this.applyExternalDataResultValues(result, itemSetId, item.privateId);
    } else if (instanceOfIExternalScriptExecutable(item)) {
      this.applyExternalProcessResultValues(result, itemSetId, param);
    }
  }

  private isConflictedRequest(request: ExternalScriptRequestInformation): boolean {
    if (!request) { return false; }
    const foundIndex = this._requestHistory.indexOf(request);
    if (foundIndex < 0) { return false; }
    for (let index = foundIndex; index < this._requestHistory.length; ++index) {
      const requested = this._requestHistory[index];
      if (requested === request) { continue; }
      if (requested.itemSetId === request.itemSetId && requested.privateId === request.privateId) { return true; }
    }
    return false;
  }

  applyExternalDataResultValues(result: ExternalScriptExecuteResult, itemSetId: number, sourcePrivateId: number): void {
    if (itemSetId < 0) { throw new Error("itemSetId is invalid!!!"); }
    if (result.values == null || result.values.length === 0) {
      LoggerFactory.getLogger().info("外部データがキャンセル終了されています。オブジェクトの値は更新されません。");
      return;
    }
    const sourceSectionIndex = this.getSectionIndexFromPrivateId(itemSetId, sourcePrivateId);
    const items = this.source.itemSets[itemSetId].items;
    for (const item of items) {
      if (!item) { continue; }
      if (!instanceOfIExternalDataApplicable(item) || item.name !== result.name) { continue; }
      if (instanceOfIFreeInputable(item) && item.shouldInputFree) { continue; }
      // V1は外部データは同一ページ内のみでリンクする模様
      const itemSectionIndex = this.getSectionIndexFromPrivateId(itemSetId, item.privateId);
      if (sourceSectionIndex != null && sourceSectionIndex !== itemSectionIndex) { continue; }
      const cloned = item.clone() as IExternalDataApplicable;
      if (ExternalScriptManager.isExternalScriptExecutableBaseItem(cloned, result.name)) {
        // 前回値CSVのパスが指定されてなかったら不要なので保存しない
        if (cloned.csvBeforeValuePath !== null && cloned.csvBeforeValuePath !== undefined) {
          cloned.previousCsvBinary = result.compressedCsvText;
        }
      }
      // TODO: サブ名称の判定したほうがよい？
      const found = result.values.find(value => item.isExternalDataCellParameterMatched(result.name, value.row, value.column));
      if (found) {
        cloned.setValueFromExternalScript(found.value);
        cloned.adjustFontSize();
      }
      this.source.updateItem(cloned, {
        itemSetId,
      });
    }
  }

  private getSectionIndexFromPrivateId(itemSetId: number, privateId: number): number {
    if (privateId == null) { return undefined; }

    for (let index = 0; index < this.source.sections.length; ++index) {
      const section = this.source.sections[index];
      if (section.itemSetId ===  itemSetId && this.hasItem(section, privateId)) { return index; }
    }
    return undefined;
  }

  private hasItem(section: Section, privateId: number): boolean {
    return !!section.itemPrivateIds.find(id => id === privateId);
  }

  private adjustObjectHeight(item: IItem): void {
    if (instanceOfIObjectHeightAdjustable(item)) {
      item.adjustObjectHeight();
    }
  }

  applyExternalProcessResultValues(result: ExternalScriptExecuteResult, itemSetId: number, param: ExternalScriptParameter): IItem[] {
    if (itemSetId < 0) { throw new Error("itemSetId is invalid!!!"); }
    if (!result || !result.isComplete || !result.scriptLinkResults) {
      throw new Error("スクリプトが正常に完了していません。");
    }
    if (result.isCancel) {
      this.hasCancel = true;
    }
    const items = this.source.itemSets[itemSetId].items;
    const updatedItems: IItem[] = [];
    for (const item of items) {
      if (instanceOfIExternalScriptAssociatable(item)) {
        // TODO:V1の仕様の確認
        const found = result.scriptLinkResults.find(r => ExternalScriptManager.isMatch(r.objectId, r.name, r.itemName, r.subName, item));
        if (!found) { continue; }
        const cloned = item.clone() as IExternalScriptAssociatable;
        cloned.setValueFromExternalScript(found.value);
        this.adjustObjectHeight(cloned);
        cloned.adjustFontSize();
        this.source.updateItem(cloned, {
          itemSetId,
        });
        updatedItems.push(cloned);
      } else if (instanceOfIExternalScriptImageAssociatable(item)) {
        const found = result.scriptLinkResults.find(r => ExternalScriptManager.isMatch(r.objectId, r.name, r.itemName, r.subName, item));
        if (!found) { continue; }
        const cloned = item.clone() as IExternalScriptImageAssociatable;
        cloned.updateImageByExternalScript(found, param.department.id);
        this.source.updateItem(cloned, {
          itemSetId,
        });
        updatedItems.push(cloned);
      }
    }
    return updatedItems;
  }

  async executeScriptInDocumentAsync(phase: EditPhaseKinds, param: ExternalScriptParameter, shouldAddToRequestStack: boolean = false): Promise<void> {
    const requests: ExternalScriptRequestInformation[] = [];
    for (let itemSetId = 0; itemSetId < this.source.itemSets.length; ++itemSetId) {
      const itemSet = this.source.itemSets[itemSetId];
      for (const item of itemSet.items) {
        if (!instanceOfIExternalScriptExecutable(item)) { continue; }
        if (!item.exePath) { continue; }
        if (this.shouldSkip(item.exePath)) { continue; }
        if (!this.isTargetPhase(item.updateKind, phase)) { continue; }
        const request = new ExternalScriptRequestInformation();
        request.itemSetId = itemSetId;
        request.privateId = item.privateId;
        if (shouldAddToRequestStack) {
          this._requestedScripts.push(request);
        } else {
          requests.push(request);
        }
      }
    }
    return this.executeRequestedScriptsAsync(param, requests);
  }

  private shouldSkip(exePath: string): boolean {
    return this.shouldRestrictScript
      && (!this.allowedScripts || (this.allowedScripts && !this.allowedScripts.some(s => !!s && exePath.match(s))));
  }

  private isTargetPhase(updateKind: UpdateKinds, phase: EditPhaseKinds): boolean {
    // TODO:V1の仕様の確認
    switch (phase) {
      case EditPhaseKinds.EditStartFirst:
        // Calculatedは厳密にはページめくって初回に表示した箇所のみキックする仕様となっている模様（V1）
        // https://groups.google.com/a/findex.co.jp/forum/#!topic/knowledge_dm/fexFXBqEjIc
        return (updateKind === UpdateKinds.Calculated || updateKind === UpdateKinds.EditStart || updateKind === UpdateKinds.ExternalDataEditStarted);
      case EditPhaseKinds.EditStart:
        // Calculatedは厳密にはページめくって初回に表示した箇所のみキックする仕様となっている模様（V1）
        return (updateKind === UpdateKinds.Calculated || updateKind === UpdateKinds.EditStart);
      case EditPhaseKinds.Calculate:
        return (updateKind === UpdateKinds.Calculated);
      case EditPhaseKinds.Save:
        return (updateKind === UpdateKinds.Calculated || updateKind === UpdateKinds.DocumentSaved || updateKind === UpdateKinds.DocumentSavedAndTemporarySaved);
      case EditPhaseKinds.TemporarySave:
        return (updateKind === UpdateKinds.Calculated || updateKind === UpdateKinds.DocumentTemporarySaved || updateKind === UpdateKinds.DocumentSavedAndTemporarySaved);
      case EditPhaseKinds.PatientInformationManualUpdate:
        return (updateKind === UpdateKinds.ExternalDataEditStarted);
      default: 
        // TODO:implement
        break;
    }
    return false;
  }

  /**
   * 計算ボタンクリック時の処理
   */
  public calculate(): void {
    for (let itemSetId = 0; itemSetId < this.source.itemSets.length; ++itemSetId) {
      this.calculateItemSetItems(itemSetId);
    }
  }

  private calculateItemSetItems(itemSetId: number): void {
    const itemSet = this.source.itemSets[itemSetId];
    if (!itemSet) { return; }
    for (const item of itemSet.items) {
      if (!instanceOfIExternalScriptExecutable(item)) { continue; }
      if (!this.isTargetPhase(item.updateKind, EditPhaseKinds.Calculate)) { continue; }
      this.pushExecuteRequest(itemSetId, item.privateId);
    }
  }

  /**
   * テンプレートをコピーはMksTemplateは対応しているがDynamicTemplateは対応していない模様
   * MksTemplateのみコピーしている
   */
  public async copyExternalTemplateIds(parameter: ExternalScriptParameter): Promise<boolean> {
    let hasError = false;
    for (let itemSetId = 0; itemSetId < this.source.itemSets.length; ++itemSetId) {
      const itemSet = this.source.itemSets[itemSetId];
      for (const item of itemSet.items) {
        if (instanceOfIMksTemplateExecutable(item) && item.mksTemplateRegisteredId) {
          const cloned = item.clone() as IMksTemplateExecutable;
          const copiedId = await this.copyMksTemplateId(parameter, item.mksTemplateRegisteredId);
          if (!copiedId) {
            hasError = true;
          }
          cloned.mksTemplateRegisteredId = copiedId;
          this.source.updateItem(cloned, {
            itemSetId,
          });
        }
        // TODO: dynamicTemplate
      }
    }
    return !hasError;
  }

  async copyMksTemplateId(parameter: ExternalScriptParameter, mksTemplateRegisteredId: string): Promise<string> {
    try {
      return await ExternalScriptManager.copyMksTemplateId(parameter, mksTemplateRegisteredId);
    } catch (e) {
      console.warn(e);
      return null;
    }
  }
}
